import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import CourseCardSkeleton from '@/features/courses/components/CourseCardSkeleton'
import { cn } from '@/lib/utils'
import { RESUME_ROW, RESUME_THUMB } from '@/screens/courses/components/home/resume-box'
import { SHELF_GRID, SHELF_MAX } from '@/screens/courses/components/shelf/shelf-box'

/** Light module only: `Skeleton` and `CourseCardSkeleton` (itself Skeleton and
 *  class strings — see its own comment), so this may sit in the route's
 *  `pendingComponent` without pulling a data-fetching component into the
 *  entry chunk. */

/** A shelf's head: the 36px row `SectionHead size="card"` draws, a bar for the words. */
function ShelfHead({ width }: { width: string }) {
  return (
    <div className="flex min-h-9 items-center">
      <Skeleton className={cn('h-5 rounded-xs', width)} />
    </div>
  )
}

function CardShelfSkeleton({ headingWidth }: { headingWidth: string }) {
  return (
    <div className="flex flex-col gap-3">
      <ShelfHead width={headingWidth} />
      <div className={SHELF_GRID}>
        {Array.from({ length: SHELF_MAX }, (_, index) => (
          <CourseCardSkeleton key={index} />
        ))}
      </div>
    </div>
  )
}

/**
 * `/courses` before its data: the richest guessed shape the page can be in —
 * the toolbar and every shelf drawn, the way `HomeSkeletons.tsx` guesses
 * Home's own optional cards. The loader awaits `home` and the catalog
 * together, so which shelves turn out to have rows is not knowable here;
 * once the page commits every conditional slot below is already decided and
 * stands still.
 */
export default function CoursesHomeSkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-8">
      <div className="flex flex-col gap-3 @md:flex-row @md:items-center @md:justify-between">
        <Skeleton className="h-11 w-full rounded-md lg:h-9 @md:w-80" />
        <Skeleton className="h-8 w-80 max-w-full rounded-md" />
      </div>

      <div className="flex flex-col gap-3">
        <ShelfHead width="w-40" />
        <div className={cn(SECTION_CARD, RESUME_ROW)}>
          <Skeleton className={cn('aspect-video rounded-md', RESUME_THUMB)} />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex h-6 items-center">
              <Skeleton className="h-4 w-3/5 rounded-xs" />
            </div>
            <Skeleton className="h-1 w-full max-w-100" />
          </div>
          <Skeleton className="h-9 w-24 shrink-0 rounded-md" />
        </div>
      </div>

      <CardShelfSkeleton headingWidth="w-32" />
      <CardShelfSkeleton headingWidth="w-28" />
      <CardShelfSkeleton headingWidth="w-32" />
    </div>
  )
}
